import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How far down the page counts as having started to read it.
 *
 * Small on purpose: the offer is meant to reach someone the moment they engage,
 * not someone who has already made up their mind at the pricing table. It is
 * one number rather than a scatter of thresholds so moving it is one edit.
 */
const SCROLL_PX = 200;

const DISMISSED = 'rvk.reading.dismissed';

/**
 * On unless a build explicitly turns it off.
 *
 * It was the other way round while Instagram answered `http_429` to every
 * handle from the deploy target's address. A relay on a residential
 * connection settled that, and the reading now answers in about three
 * seconds with the script following.
 *
 * The polarity matters more than the value: as an opt-in, no committed script
 * set the variable, so any deploy that forgot it switched the offer off with
 * nothing failing anywhere. A kill switch has to be pulled on purpose.
 */
function enabled(): boolean {
  return import.meta.env.VITE_PROFILE_READING_ENABLED !== 'false';
}

export type InviteSource = 'scroll';

export interface ProfileReadingInvite {
  open: boolean;
  invite: (source: InviteSource) => void;
  dismiss: () => void;
}

export interface ProfileReadingInviteOptions {
  /** Reported so the funnel is measurable without this hook knowing PostHog. */
  onEvent?: (name: string, properties?: Record<string, unknown>) => void;
}

/**
 * When the sales page offers to read her profile.
 *
 * It opens once, on the first real scroll — the page has to have been looked at
 * before it interrupts, and scrolling is the cheapest evidence of that. A modal
 * that returns after being closed teaches people to close modals without
 * reading them, so a dismissal is remembered for the session.
 */
export function useProfileReadingInvite(
  options: ProfileReadingInviteOptions = {}
): ProfileReadingInvite {
  const [open, setOpen] = useState(false);
  const [offered, setOffered] = useState(false);

  // Held in a ref as well as in state: the scroll listener is registered once
  // and would otherwise close over the first render's `false` forever.
  const onEvent = useRef(options.onEvent);
  onEvent.current = options.onEvent;

  const invite = useCallback((source: InviteSource) => {
    setOffered(true);
    setOpen(true);
    onEvent.current?.('profile_reading_offered', { source });
  }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    onEvent.current?.('profile_reading_dismissed');
    try {
      sessionStorage.setItem(DISMISSED, '1');
    } catch {
      // Private browsing refuses the write. Losing the memory of a dismissal is
      // a worse modal, not a broken page.
    }
  }, []);

  useEffect(() => {
    if (!enabled() || offered) {
      return;
    }
    try {
      if (sessionStorage.getItem(DISMISSED) === '1') {
        return;
      }
    } catch {
      // Same as above: unreadable storage means it opens, not that it breaks.
    }

    const onScroll = () => {
      if (window.scrollY > SCROLL_PX) {
        invite('scroll');
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Someone who reloads mid-page, or lands on an anchor, is already past the
    // threshold and would never fire a scroll event from there.
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [offered, invite]);

  return { open, invite, dismiss };
}
